import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import Login from '../components/Login';
import Dashboard from '../components/Dashboard';
import PrivateRoute from '../components/PrivateRoute';
import { CircularProgress, Box } from '@mui/material';
import QuestionBankPage from '../pages/QuestionBankPage';
import QuestionDetailPage from '../pages/QuestionDetailPage';
import QuestionnaireAnalysisPage from '../pages/QuestionnaireAnalysisPage';
import TestSuccessPage from '../pages/TestSuccessPage';
import { lazyWithRetry } from '../utils/lazyWithRetry';

const CreateTestPage = lazyWithRetry(() => import('../pages/CreateTestPage'));
const EditQuestionPage = lazyWithRetry(() => import('../pages/EditQuestionPage'));
const AnalysisPage = lazyWithRetry(() => import('../pages/AnalysisPage'));
const AdServicePage = lazyWithRetry(() => import('../pages/AdServicePage'));
const UserManagementPage = lazyWithRetry(() => import('../pages/UserManagementPage'));
const OverallAnalysisPage = lazyWithRetry(() => import('../pages/OverallAnalysisPage'));
const SubjectAnalysisPage = lazyWithRetry(() => import('../pages/SubjectAnalysisPage'));
const AddQuestionPage = lazyWithRetry(() => import('../pages/AddQuestionPage'));
const NewUsersPage = lazyWithRetry(() => import('../pages/NewUsersPage'));
const LearningPathwayAttachmentsPage = lazyWithRetry(() => import('../pages/LearningPathwayAttachmentsPage'));
const AddAdPage = lazyWithRetry(() => import('../pages/AddAdPage'));
const ManageInstitutionsPage = lazyWithRetry(() => import('../pages/ManageInstitutionsPage'));
const AddInstitutionPage = lazyWithRetry(() => import('../pages/AddInstitutionPage'));
const EditInstitutionPage = lazyWithRetry(() => import('../pages/EditInstitutionPage'));
const MoveQuestionPage = lazyWithRetry(() => import('../pages/MoveQuestionPage'));
const TempUsersPage = lazyWithRetry(() => import('../pages/TempUsersPage'));
const GuestEntryPage = lazyWithRetry(() => import('../pages/GuestEntryPage'));
const GuestTestPage = lazyWithRetry(() => import('../pages/GuestTestPage'));
const GuestResultPage = lazyWithRetry(() => import('../pages/GuestResultPage'));
const ConfigurationPage = lazyWithRetry(() => import('../pages/ConfigurationPage'));
const HtmlEditorPage = lazyWithRetry(() => import('../pages/HtmlEditorPage'));
const ColourConfigurationPage = lazyWithRetry(() => import('../pages/ColourConfigurationPage'));
const ThemeDetailPage = lazyWithRetry(() => import('../pages/ThemeDetailPage'));
const RankListPage = lazyWithRetry(() => import('../pages/RankListPage'));
const PrivilegeListPage = lazyWithRetry(() => import('../pages/PrivilegeListPage'));
// const ExcelSequenceChangerPage = lazyWithRetry(() => import('../pages/ExcelSequenceChangerPage'));
const ApiAnalysisPage = lazyWithRetry(() => import('../pages/ApiAnalysisPage'));
const ProductsPage = lazyWithRetry(() => import('../pages/ProductsPage'));
const OrdersPage = lazyWithRetry(() => import('../pages/OrdersPage'));
const OrderDetailPage = lazyWithRetry(() => import('../pages/OrderDetailPage'));
const MockTestManagementPage = lazyWithRetry(() => import('../pages/MockTestManagementPage'));
const StudentsManagementPage = lazyWithRetry(() => import('../pages/StudentsManagementPage'));
const StudentDetailsPreviewPage = lazyWithRetry(() => import('../pages/StudentDetailsPreviewPage'));
const AddProductPage = lazyWithRetry(() => import('../pages/AddProductPage'));
const EditProductPage = lazyWithRetry(() => import('../pages/EditProductPage'));
const UserAnalysisPage = lazyWithRetry(() => import('../pages/UserAnalysisPage'));
const NotificationManagementPage = lazyWithRetry(() => import('../pages/NotificationManagementPage'));

const AppRoutes = () => (
  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/:institution" element={<Login />} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/create-test" element={<PrivateRoute><CreateTestPage /></PrivateRoute>} />
      <Route path="/add-question" element={<PrivateRoute><AddQuestionPage /></PrivateRoute>} />
      <Route path="/edit-question" element={<PrivateRoute><EditQuestionPage /></PrivateRoute>} />
      <Route path="/new-users" element={<PrivateRoute><NewUsersPage /></PrivateRoute>} />
      <Route path="/temp-users" element={<PrivateRoute><TempUsersPage /></PrivateRoute>} />
      <Route path="/guest/:testName/:testId" element={<GuestEntryPage />} />
      <Route path="/question/:testId" element={<GuestTestPage />} />
      <Route path="/result" element={<GuestResultPage />} />
      <Route path="/attachments" element={<PrivateRoute><LearningPathwayAttachmentsPage /></PrivateRoute>} />
      <Route path="/analysis" element={<PrivateRoute><AnalysisPage /></PrivateRoute>} />
      <Route path="/analysis/:id" element={<PrivateRoute><QuestionnaireAnalysisPage /></PrivateRoute>} />
      <Route path="/ad-service" element={<PrivateRoute><AdServicePage /></PrivateRoute>} />
      <Route path="/ad-service/new" element={<PrivateRoute><AddAdPage /></PrivateRoute>} />
      <Route path="/user-management" element={<PrivateRoute><UserManagementPage /></PrivateRoute>} />
      <Route path="/question-bank" element={<PrivateRoute><QuestionBankPage /></PrivateRoute>} />
      <Route path="/question-detail" element={<PrivateRoute><QuestionDetailPage /></PrivateRoute>} />
      <Route path="/overall-analysis" element={<PrivateRoute><OverallAnalysisPage /></PrivateRoute>} />
      <Route path="/overall-analysis/:subCode" element={<PrivateRoute><SubjectAnalysisPage /></PrivateRoute>} />
      <Route path="/test-success" element={<PrivateRoute><TestSuccessPage /></PrivateRoute>} />
      <Route path="/manage-institutions" element={<PrivateRoute><ManageInstitutionsPage /></PrivateRoute>} />
      <Route path="/add-institution" element={<PrivateRoute><AddInstitutionPage /></PrivateRoute>} />
      <Route path="/edit-institution" element={<PrivateRoute><EditInstitutionPage /></PrivateRoute>} />
      <Route path="/move-question" element={<PrivateRoute><MoveQuestionPage /></PrivateRoute>} />
      <Route path="/configuration" element={<PrivateRoute><ConfigurationPage /></PrivateRoute>} />
      <Route path="/colour-configuration" element={<PrivateRoute><ColourConfigurationPage /></PrivateRoute>} />
      <Route path="/colour-configuration/:appType" element={<PrivateRoute><ThemeDetailPage /></PrivateRoute>} />
      <Route path="/html-editor" element={<PrivateRoute><HtmlEditorPage /></PrivateRoute>} />
      <Route path="/rank-list" element={<PrivateRoute><RankListPage /></PrivateRoute>} />
      <Route path="/privilege-management" element={<PrivateRoute><PrivilegeListPage /></PrivateRoute>} />
      {/* <Route path="/excel-sequence-changer" element={<PrivateRoute><ExcelSequenceChangerPage /></PrivateRoute>} /> */}
      <Route path="/api-analysis" element={<PrivateRoute><ApiAnalysisPage /></PrivateRoute>} />
      <Route path="/products" element={<PrivateRoute><ProductsPage /></PrivateRoute>} />
      <Route path="/products/new" element={<PrivateRoute><AddProductPage /></PrivateRoute>} />
      <Route path="/products/edit/:id" element={<PrivateRoute><EditProductPage /></PrivateRoute>} />
      <Route path="/orders" element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
      <Route path="/orders/:id" element={<PrivateRoute><OrderDetailPage /></PrivateRoute>} />
      <Route path="/mock-test-management" element={<PrivateRoute><MockTestManagementPage /></PrivateRoute>} />
      <Route path="/students-management" element={<PrivateRoute><StudentsManagementPage /></PrivateRoute>} />
      <Route path="/students-management/:id" element={<PrivateRoute><StudentDetailsPreviewPage /></PrivateRoute>} />
      <Route path="/user-analysis/:id" element={<PrivateRoute><UserAnalysisPage /></PrivateRoute>} />
      <Route path="/notifications" element={<PrivateRoute><NotificationManagementPage /></PrivateRoute>} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
